import { EOriginSystemId, EPaymentFrequency } from '@if/core/types'
import { formatCurrency, formatDate, formatPercentage } from '@if/core/utils'
import { ActionList, Caption, Icon } from '@if/mui'
import {
  Box,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material'
import { Check, CircleAlert, CircleCheck, Star } from 'lucide-react'
import { useMerchantFinancingConfig } from '@src/data/api/configs-api/financing-config-api'
import { useUpdateMerchantPaymentPlan } from '@src/data/api/credit-api/credit-api'
import { useCalculateTermAndPlanOptions } from '@src/data/api/worksheet-api/term-plan-api'
import { getDisplayedMaxAmountFinanced } from '@src/data/creditapp-selectors'
import { CreditApplication } from '@src/types/CreditApplicationSchema'
import { Merchant } from '@src/types/Merchant'
import { CalculateTermPlanOptionsDto, TermAndPlanOption } from '@src/types/TermPlanOptionDto'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMappedTermAndPlanOptions } from '../../worksheet-hooks'

interface Props {
  onApplied: () => void
  creditApp: CreditApplication
  merchant: Merchant
}

interface PlanRowProps {
  option: TermAndPlanOption
  stars: number | undefined
  onApply: (paymentPlanId: string) => void
  isApplying: boolean
}

const PlanRow = ({ option, stars, onApply, isApplying }: PlanRowProps) => {
  const { t } = useTranslation()

  return (
    <TableRow>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Caption>{option.planLabel ?? t('common.regularPlan')}</Caption>
          {stars != null && (
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
              ({stars}
              <Icon icon={Star} size="xs" />
              {stars < 5 && '+'})
            </Box>
          )}
        </Box>
      </TableCell>
      <TableCell align="right">
        <Caption>{`${option.termMonths} ${t('common.months')}`}</Caption>
      </TableCell>
      <TableCell align="right">
        <Caption>{option.planLabel ? `${formatPercentage(option.merchantFeeRate)}%` : '—'}</Caption>
      </TableCell>
      <TableCell align="right">
        <Caption>{formatCurrency(option.monthlyPayment)}</Caption>
      </TableCell>
      <TableCell align="center">
        {option.disabled ? (
          <Icon icon={CircleAlert} color="error" size="md" />
        ) : (
          <Icon icon={CircleCheck} color="success" size="md" />
        )}
      </TableCell>
      <TableCell align="right">
        {!option.disabled && (
          <ActionList
            actions={[
              {
                icon: Check,
                label: t('viewCreditApplication.applyPlan'),
                disabled: isApplying,
                onClick: () => onApply(option.paymentPlanId),
              },
            ]}
            variant="button"
            menuLabel={t('viewCreditApplication.applyPlan')}
          />
        )}
      </TableCell>
    </TableRow>
  )
}

const PaymentPlanSimulator = ({ onApplied, creditApp, merchant }: Props) => {
  const { t } = useTranslation()
  const [financingConfig] = useMerchantFinancingConfig(merchant.id, creditApp.financingProgramId)
  const maxAmount =
    getDisplayedMaxAmountFinanced(
      creditApp.financingProgramId,
      creditApp.finalCreditDecision.maxAmountFinanced,
      financingConfig,
    ) ?? 0
  const maxPmt = creditApp.finalCreditDecision.maxPmtAmount

  const [amountRequested, setAmountRequested] = useState<number>(creditApp.requestedLoanAmount ?? maxAmount)
  const [updateMerchantPaymentPlan, isUpdatingPlan] = useUpdateMerchantPaymentPlan()

  const promoCodeMinStars = useMemo(() => {
    const map = new Map<string, number>()
    if (!merchant?.planPromoRules) return map
    for (const rule of merchant.planPromoRules) {
      if (!/^\*+$/.test(rule.planName)) continue
      const stars = rule.planName.length
      const codes = [rule.defaultPromoCode, ...(rule.selectablePromoCodes ?? [])]
      for (const code of codes) {
        const current = map.get(code)
        if (current === undefined || stars < current) map.set(code, stars)
      }
    }
    return map
  }, [merchant?.planPromoRules])

  const isAmountValid = amountRequested > 0 && amountRequested <= maxAmount
  const today = formatDate(new Date())
  const firstPaymentOn = formatDate(new Date(new Date().setDate(new Date().getDate() + 30)))

  const dto = useMemo<CalculateTermPlanOptionsDto | null>(() => {
    if (!isAmountValid) return null
    return {
      creditApplicationId: creditApp.id,
      merchantId: creditApp.merchantId,
      interestRate: creditApp.finalCreditDecision.interestRate,
      hasCoapplicant: creditApp.coApplicant !== null,
      stateIso: creditApp.applicant.currentAddress.stateIso!,
      paymentFrequency: EPaymentFrequency.Monthly,
      deliveryOn: today,
      firstPaymentOn,
      maxPmtAmount: maxPmt,
      amountRequested,
      includeInsurance: false,
      originSystemId: EOriginSystemId.MerchantDashboard,
    }
  }, [isAmountValid, creditApp, today, firstPaymentOn, maxPmt, amountRequested])

  const [rows, isLoadingRows] = useCalculateTermAndPlanOptions(dto)
  const { options, isLoading } = useMappedTermAndPlanOptions(rows, isLoadingRows)

  const handleApply = async (paymentPlanId: string) => {
    await updateMerchantPaymentPlan({
      creditApplicationId: creditApp.id,
      financingProgramId: creditApp.financingProgramId,
      merchantPaymentPlanId: paymentPlanId,
    })
    onApplied()
  }

  return (
    <>
      <Stack direction="row" spacing={2} sx={{ mt: 1, mb: 2 }} alignItems="center">
        <Caption sx={{ whiteSpace: 'nowrap' }}>
          {t('viewCreditApplication.maxMonthlyPayment')}: <strong>{formatCurrency(maxPmt)}</strong>
        </Caption>
        <TextField
          label={t('editCreditApplication.amountRequested')}
          type="number"
          value={amountRequested}
          onChange={(e) => setAmountRequested(Number(e.target.value))}
          error={!isAmountValid}
          helperText={!isAmountValid ? `Max: ${formatCurrency(maxAmount)}` : undefined}
          slotProps={{ htmlInput: { min: 0, max: maxAmount, step: 100 } }}
          size="small"
          fullWidth
        />
      </Stack>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('worksheet.paymentPlan')}</TableCell>
              <TableCell align="right">{t('viewCreditApplication.maxTerm')}</TableCell>
              <TableCell align="right">{t('viewCreditApplication.merchantFee')}</TableCell>
              <TableCell align="right">{t('viewCreditApplication.monthlyPayment')}</TableCell>
              <TableCell align="center" />
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {isAmountValid && isLoading && options.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <CircularProgress size={18} />
                </TableCell>
              </TableRow>
            )}
            {isAmountValid &&
              options.map((option) => (
                <PlanRow
                  key={option.key}
                  option={option}
                  stars={
                    option.planLabel
                      ? (promoCodeMinStars.get(option.planLabel) ??
                        promoCodeMinStars.get(option.planLabel.replace('PLAN', '').trim()))
                      : undefined
                  }
                  onApply={handleApply}
                  isApplying={isUpdatingPlan}
                />
              ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  )
}

export default PaymentPlanSimulator
